export const TARGET_METRICS = ['finished_visits', 'signed_ccv', 'sent_offers'];

export const TARGET_PERIOD_TYPES = ['monthly', 'weekly', 'quarterly'];

export const DEFAULT_TARGET_VALUES = {
  finished_visits: 23,
  signed_ccv: 1,
  sent_offers: 5,
};

export const TARGET_TRACKING_EVENTS = {
  set: 'lais_target_set',
  bulkApplied: 'lais_target_bulk_applied',
  reset: 'lais_target_reset_to_default',
};

export function todayDateKey(date = new Date()) {
  return date.toISOString().slice(0, 10);
}

function dateFromKey(value = todayDateKey()) {
  if (value instanceof Date) {
    return new Date(
      Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate())
    );
  }

  const [year, month, day] = String(value || todayDateKey())
    .slice(0, 10)
    .split('-')
    .map(Number);

  if (!year || !month || !day) {
    return dateFromKey(todayDateKey());
  }

  return new Date(Date.UTC(year, month - 1, day));
}

function dateKeyFromDate(value) {
  return value.toISOString().slice(0, 10);
}

function addDays(value, days) {
  const next = new Date(value);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function startOfWeek(value) {
  const day = value.getUTCDay() || 7;
  return addDays(value, 1 - day);
}

function startOfQuarter(value) {
  const quarterMonth = Math.floor(value.getUTCMonth() / 3) * 3;
  return new Date(Date.UTC(value.getUTCFullYear(), quarterMonth, 1));
}

function endOfMonth(value) {
  return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth() + 1, 0));
}

function pad2(value) {
  return String(value).padStart(2, '0');
}

function shortDayMonth(value) {
  return `${pad2(value.getUTCDate())}/${pad2(value.getUTCMonth() + 1)}`;
}

export function defaultTargetValue(metric) {
  return DEFAULT_TARGET_VALUES[metric] ?? 0;
}

export function periodStartFor(periodType, date = todayDateKey()) {
  const targetDate = dateFromKey(date);

  if (periodType === 'weekly') {
    return dateKeyFromDate(startOfWeek(targetDate));
  }

  if (periodType === 'quarterly') {
    return dateKeyFromDate(startOfQuarter(targetDate));
  }

  return dateKeyFromDate(
    new Date(Date.UTC(targetDate.getUTCFullYear(), targetDate.getUTCMonth(), 1))
  );
}

export function periodEndFor(periodType, date = todayDateKey()) {
  const startDate = dateFromKey(periodStartFor(periodType, date));

  if (periodType === 'weekly') {
    return dateKeyFromDate(addDays(startDate, 6));
  }

  if (periodType === 'quarterly') {
    return dateKeyFromDate(
      new Date(
        Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth() + 3, 0)
      )
    );
  }

  return dateKeyFromDate(endOfMonth(startDate));
}

export function periodRangeLabel(periodType, date = todayDateKey()) {
  const start = dateFromKey(periodStartFor(periodType, date));
  const end = dateFromKey(periodEndFor(periodType, date));

  if (start.getUTCMonth() === end.getUTCMonth()) {
    return `${pad2(start.getUTCDate())}–${pad2(end.getUTCDate())}/${pad2(
      end.getUTCMonth() + 1
    )}`;
  }

  return `${shortDayMonth(start)}–${shortDayMonth(end)}`;
}

export function normalizeTargetsResponse(response = {}) {
  const payload = response.data || response;

  return {
    rows: Array.isArray(payload) ? payload : payload.data || [],
    meta: payload.meta || {},
  };
}

export function countBehindPace(rows = []) {
  return rows.filter(row => Boolean(row.behind_pace || row.target?.behind_pace))
    .length;
}

export function progressWidth(value) {
  return `${Math.min(Math.max(Number(value || 0), 0), 100)}%`;
}

export function targetDisplayName(row = {}) {
  return row.user?.name || row.team?.name || '';
}

export function targetSubjectId(row = {}) {
  return row.user?.id || row.team?.id || null;
}

export function targetScope(row = {}) {
  return row.team ? 'team' : 'agent';
}

export function trackingPayloadForRow(row = {}, extras = {}) {
  return {
    scope: targetScope(row),
    userId: row.user?.id,
    teamId: row.team?.id,
    metric: row.target?.metric,
    periodType: row.target?.period_type,
    periodStart: row.target?.period_start,
    targetValue: row.target?.target_value,
    source: row.target?.source,
    ...extras,
  };
}
